import type { ActionContext } from 'vuex';

import constants from '../../constants';
import { FILTER_NAVIGATION_MUTATIONS } from './name';
import type { TFilterNavigationState, TNavigationType, TPageKey, TSavedPageFilters, TTransferFilters } from './types';

const NAVIGATION_TYPE = constants.NAVIGATION_TYPE as Record<string, TNavigationType>;
const { PAGE_KEY, FILTER_STORAGE_KEY } = constants;

type TContext = ActionContext<TFilterNavigationState, unknown>;

// Страницы, для которых фильтры сохраняются. Статистика всегда сбрасывается.
const SAVEABLE_PAGES = new Set<TPageKey>([PAGE_KEY.LIST, PAGE_KEY.MAP] as TPageKey[]);

const resolveStorageKey = (pageKey: TPageKey): string | undefined =>
  FILTER_STORAGE_KEY[pageKey as keyof typeof FILTER_STORAGE_KEY];

const CARD_PATH_PREFIXES = ['/mpkProjects/view/', '/mpkProjects/edit/'];

// Определяет тип навигации по приоритету:
// BUTTON  - если перед переходом был вызван setTransferFilters (pending установлен компонентом)
// BACK    - возврат из карточки проекта или браузерное назад (предпоследний маршрут совпадает с целевым)
// SIDEBAR - все остальные случаи (сайдбар, внешняя ссылка, первый вход)
const detectNavigationType = (
  toPath: string,
  fromPath: string,
  routeHistory: string[],
  hasPendingTransfer: boolean,
): TNavigationType => {
  if (hasPendingTransfer) return NAVIGATION_TYPE.BUTTON;
  if (CARD_PATH_PREFIXES.some((p) => fromPath.startsWith(p))) return NAVIGATION_TYPE.BACK;
  if (routeHistory.at(-2) === toPath) return NAVIGATION_TYPE.BACK;
  return NAVIGATION_TYPE.SIDEBAR;
};

// Вызывается router guard-ом при каждом переходе на страницы МПК-проектов
// Фиксирует тип навигации и добавляет маршрут в историю
export const onNavigation = ({ commit, state }: TContext, payload: { toPath: string; fromPath: string }): void => {
  const navigationType = detectNavigationType(payload.toPath, payload.fromPath, state.routeHistory, !!state.pendingTransfer);
  commit(FILTER_NAVIGATION_MUTATIONS.setNavigationType, navigationType);
  commit(FILTER_NAVIGATION_MUTATIONS.pushRoute, payload.toPath);
};

// Вызывается компонентом перед router.push при переходе по кнопке
// сохраняет фильтры, которые целевая страница получит через consumeFilters()
export const setTransferFilters = (
  { commit }: TContext,
  payload: { targetPage: TPageKey; filters: TTransferFilters },
): void => {
  commit(FILTER_NAVIGATION_MUTATIONS.setPendingTransfer, {
    targetPage: payload.targetPage,
    filters: payload.filters,
  });
};

// Сохраняет текущие фильтры страницы в store и localStorage
// вызывается при каждом применении фильтра, чтобы восстановить состояние при следующем входе через сайдбар
export const savePageFilters = (
  { commit }: TContext,
  payload: { pageKey: TPageKey; filters: TSavedPageFilters },
): void => {
  if (!SAVEABLE_PAGES.has(payload.pageKey)) return;

  const storageKey = resolveStorageKey(payload.pageKey);
  if (storageKey) localStorage.setItem(storageKey, JSON.stringify(payload.filters));

  commit(FILTER_NAVIGATION_MUTATIONS.setPageFilters, payload);
};
